import { ENV_VARS } from '@/env';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import { Col, Divider, Form, Popconfirm, Row } from 'antd';
import { t } from 'i18next';
import { memo, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { getMilageByStopsInSuccess } from '../../../../../../../redux/shipment/ShipmentActions';
import Button from '../../../../../../components/Buttons/Button';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import TransportationModeSelector from '../../../../../../components/Selectors/TransportationModeSelector';
import HallTransportationDetailsCard from './HallTransportationDetailsCard';
import {
  additionalStopsValidation,
  distanceRules,
  initialValues,
  sequenceStopSorting,
  transportationModeRules,
} from './constants';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';

const HallTransportationDetails = () => {
  const dispatch = useDispatch();
  const { getMilageByStops } = useSelector((state) => state.shipment);
  const [form] = Form.useForm();
  const formStops = Form.useWatch('stops', form);

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        { name: 'transportationMode', value: data.transportationMode },
        { name: 'distance', value: data.distance },
        {
          name: 'stops',
          value:
            data.transportationDetails?.sort(sequenceStopSorting)?.map((item) => ({
              stopTypeId: item.stopTypeId,
              stopSequence: item.stopSequence,
              countryCode: item.countryCode,
              postalCode: item.portRecKey,
              portRecKey: item.portRecKey,
              portState: item.portState,
              portCity: item.portCity,
              portType: item.portType,
              portName: item.portName,
              address: item.address,
              editMode: true,
            })) || [],
        },
      ]);
    }
  }, []);

  const handleDataChange = (data, index) => {
    const stopsLocal = form.getFieldValue('stops');
    stopsLocal[index] = { ...stopsLocal[index], ...data };
    form.setFieldValue('stops', stopsLocal);
  };

  const selectedStopsIndex =
    formStops?.map(({ selected }, index) => (selected ? index : null))?.filter((val) => val !== null) || [];

  const addStop = () => {
    const stopsLocal = form.getFieldValue('stops') || [];
    const stopSequence = stopsLocal.length + 1;

    stopsLocal.push({
      stopTypeId: stopSequence === 1 ? '1' : null,
      editMode: true,
      isNew: true,
      countryCode: 'US',
      gridOperation: 1,
      stopSequence,
    });
    form.setFieldValue('stops', stopsLocal);
  };

  const removeStop = (indexes) => {
    let stopsLocal = form.getFieldValue('stops') || [];
    indexes.forEach((index) => {
      if (index >= 0) {
        stopsLocal.splice(index, indexes.length);
      }
    });
    stopsLocal = stopsLocal.reduce((acc, item, currentIndex) => {
      const stopSequence = currentIndex + 1;

      return [
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...acc,
        {
          ...item,
          stopTypeId: stopSequence === 1 ? '1' : item.stopTypeId,
          stopSequence: stopSequence,
        },
      ];
    }, []);

    form.setFieldValue('stops', stopsLocal);
  };

  useEffect(() => {
    return () => {
      dispatch(getMilageByStopsInSuccess({ totalMilageInMiles: 0, totalMilageInKm: 0 }));
    };
  }, [dispatch]);

  return (
    <Form name="stopsForm" form={form} initialValues={initialValues}>
      <Row justify="space-between" align="middle">
        <Col flex={1}>
          <Row gutter={15} align="middle">
            <Col span={8}>
              <TransportationModeSelector
                dataTestId="transportation-mode-field"
                placeholder="Select transportation mode"
                name="transportationMode"
                label={
                  <FormLabel className="f-12 fw-400">
                    {t('translation:getQuote.transportationDetails.transportationMode.label')}
                  </FormLabel>
                }
                editMode
                margin="m-0"
                singleLineMode
                rules={transportationModeRules(t)}
              />
            </Col>
            <Col data-testid="transportation-pick-up">
              <FormLabel>{t('translation:default.fields.pickUp')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
            </Col>
            <Col data-testid="transportation-delivery">
              <FormLabel>{t('translation:getQuote.transportationDetails.delivery')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
            </Col>
            <Col data-testid="transportation-total-distance-miles">
              <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceMiles')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">
                <NumericFormat
                  thousandSeparator
                  thousandsGroupStyle="lakh"
                  value={String(getMilageByStops?.totalMilageInMiles)}
                  displayType="text"
                  valueIsNumericString
                  decimalScale={2}
                  data-testid="total-distance-miles"
                />
              </SecondaryLabel>
            </Col>
            <Col data-testid="transportation-total-distance-km">
              <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceKm')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">
                <NumericFormat
                  thousandSeparator
                  thousandsGroupStyle="lakh"
                  value={String(getMilageByStops?.totalMilageInKm)}
                  displayType="text"
                  valueIsNumericString
                  decimalScale={2}
                  data-testid="total-distance-km"
                />
              </SecondaryLabel>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={15}>
            <Col className="flex-1">
              <FormInput
                name="distance"
                inputType="number"
                label={`${t('translation:default.fields.distance.placeholder')} :`}
                placeholder={t('translation:default.fields.distance.placeholder')}
                margin="m-0"
                singleLineMode
                editMode
                rules={distanceRules}
                dataTestId="distance-field"
              />
            </Col>
            <Col>
              <Button
                dataTestId="transportation-add-stop-button"
                className={isHalliburton ? 'hb-button' : ''}
                onClick={addStop}
              >
                {t('translation:getQuote.transportationDetails.buttons.addStop')}
              </Button>
            </Col>
            <Col>
              <Popconfirm
                placement="topRight"
                title="Are you sure you want to delete the stop?"
                onConfirm={() => {
                  removeStop(selectedStopsIndex);
                }}
                okText="Yes"
                cancelText="No"
                overlayClassName="delete-popup-container"
              >
                <Button
                  dataTestId="transportation-delete-stop-button"
                  variant="danger"
                  type="default"
                  disabled={selectedStopsIndex.length === 0}
                >
                  {t('translation:getQuote.transportationDetails.buttons.deleteStop')}
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        </Col>
      </Row>
      <Form.List name="stops">
        {(fields) => (
          <div className="w-100">
            <Divider className="border-top-border my-15" style={{ width: '120%', marginLeft: '-10%' }} />
            <div>
              {fields.map(({ key, name, ...restField }, index) => (
                <HallTransportationDetailsCard
                  key={key}
                  name={name}
                  index={index}
                  stopDetails={formStops?.[name]}
                  handleDataChange={handleDataChange}
                  isLast={fields.length === index + 1}
                  form={form}
                  totalStops={fields.length}
                  {...restField}
                />
              ))}
            </div>
          </div>
        )}
      </Form.List>
      <HiddenFormInput
        name="additionalStopsValidation"
        className="my-15"
        rules={additionalStopsValidation(form)}
        dataTestId="additional-stops-validation"
      />
    </Form>
  );
};

export default memo(HallTransportationDetails);
